<template>
  <div class="login-wrapper">
    <!-- Left Side - Lottie Animation -->
    <div class="login-left">
      <div class="illustration-container">
        <Vue3Lottie 
          animationLink="/assets/login-b.json"
          :height="800" 
          :width="800"
          :loop="true"
          :autoPlay="true"
        />
      </div>

   
    </div>

    <!-- Right Side - Login Form -->
    <div class="login-right">
      <!-- Background Watermark Logo -->
      <div class="watermark-logo-login">
        <img src="/assets/img/sudevwa-nobg.png" alt="SUDEVWA Watermark" />
        <h1 class="watermark-text-login">SUDEVWA</h1>
      </div>

      <div class="login-form-container">
        <div class="hero-heading-login">
          <template v-if="isLogin">
            <h2 class="hero-title-login">
              <div class="hero-prefix-login">
                <span>Connect</span>
                <span>Your</span>
              </div>
              <div class="hero-whatsapp-group-login">
                <div class="hero-whatsapp-main-login">
                  <i class="bi bi-whatsapp hero-icon-login"></i>
                  <span class="highlight-login">WhatsApp</span>
                </div>
                <span class="hero-suffix-login">
                  with 
                  <img src="/assets/img/sudevwa-nobg.png" alt="SUDEVWA" class="hero-logo-small-login" />
                  SUDEVWA
                </span>
              </div>
            </h2>
            <p class="hero-subtitle-login">Sign in to manage your instances and start messaging</p>
          </template>
          <template v-else>
            <h2 class="hero-title-login">
              <div class="hero-prefix-login">
                <span>Start</span>
                <span>Managing</span>
              </div>
              <div class="hero-whatsapp-group-login">
                <div class="hero-whatsapp-main-login">
                  <i class="bi bi-whatsapp hero-icon-login"></i>
                  <span class="highlight-login">WhatsApp</span>
                </div>
                <span class="hero-suffix-login">
                  with 
                  <img src="/assets/img/sudevwa-nobg.png" alt="SUDEVWA" class="hero-logo-small-login" />
                  SUDEVWA
                </span>
              </div>
            </h2>
            <p class="hero-subtitle-login">Create your account to connect instances</p>
          </template>
        </div>

        <!-- Toggle Buttons -->
        <div class="auth-toggle-login mb-3" :class="{ 'register-active': !isLogin }">
          <button 
            type="button"
            :class="['toggle-btn-login', { active: isLogin }]"
            @click="switchToLogin"
          >
            <i class="bi bi-box-arrow-in-right me-2"></i>Login
          </button>
          <button 
            type="button"
            :class="['toggle-btn-login', { active: !isLogin }]"
            @click="switchToRegister"
          >
            <i class="bi bi-person-plus me-2"></i>Register
          </button>
        </div>

        <!-- Login Form -->
        <form v-if="isLogin" @submit.prevent="handleLogin">
          <div class="form-group">
            <label for="username">Username</label>
            <input
              type="text"
              id="username"
              v-model="credentials.username"
              placeholder="Username"
              required
            />
          </div>

          <div class="form-group">
            <label for="password">Password</label>
            <input
              type="password"
              id="password"
              v-model="credentials.password"
              placeholder="Password"
              required
            />
          </div>

          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <button type="submit" class="btn-login" :disabled="isLoading">
            {{ isLoading ? 'Loading...' : 'Log In' }}
          </button>
        </form>

        <!-- Register Form -->
        <form v-else @submit.prevent="handleRegister">
          <div class="row g-3">
            <div class="col-md-6">
              <div class="form-group">
                <label for="reg_username">Username *</label>
                <input
                  type="text"
                  id="reg_username"
                  v-model="registerData.username"
                  placeholder="Username (min 3 chars)"
                  required
                  @blur="validateField('username')"
                />
                <div v-if="errors.username" class="error-text-login">{{ errors.username }}</div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group">
                <label for="reg_email">Email *</label>
                <input
                  type="text"
                  id="reg_email"
                  v-model="registerData.email"
                  placeholder="your.email@example.com"
                  required
                  @blur="validateField('email')"
                />
                <div v-if="errors.email" class="error-text-login">{{ errors.email }}</div>
              </div>
            </div>

            <div class="col-12">
              <div class="form-group">
                <label for="reg_fullname">Full Name *</label>
                <input
                  type="text"
                  id="reg_fullname"
                  v-model="registerData.full_name"
                  placeholder="Your Full Name"
                  required
                />
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group">
                <label for="reg_password">Password *</label>
                <input
                  type="password"
                  id="reg_password"
                  v-model="registerData.password"
                  placeholder="Min 8 characters"
                  required
                  @input="checkPasswordStrength"
                />
                <div v-if="errors.password" class="error-text-login">{{ errors.password }}</div>
                
                <!-- Password Strength -->
                <div v-if="registerData.password" class="password-strength-login">
                  <div class="strength-bar-login">
                    <div 
                      class="strength-fill-login" 
                      :class="passwordStrength"
                      :style="{ width: passwordStrength === 'weak' ? '33%' : passwordStrength === 'medium' ? '66%' : '100%' }"
                    ></div>
                  </div>
                  <small class="strength-text-login" :class="passwordStrength">
                    {{ passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1) }}
                  </small>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="form-group">
                <label for="reg_confirm">Confirm Password *</label>
                <input
                  type="password"
                  id="reg_confirm"
                  v-model="confirmPassword"
                  placeholder="Re-enter your password"
                  required
                />
                <div v-if="errors.confirm_password" class="error-text-login">{{ errors.confirm_password }}</div>
              </div>
            </div>
          </div>

          <div v-if="errorMessage" class="error-message mt-3">
            {{ errorMessage }}
          </div>

          <button type="submit" class="btn-login mt-3 w-100" :disabled="isLoading">
            {{ isLoading ? 'Creating Account...' : 'Create Account' }}
          </button>
        </form>
      </div>
    </div>
  </div>
  <div v-if="showLoginSuccess" class="login-success-overlay">
    <Vue3Lottie  
      animationLink="/assets/success.json"
      :height="250"
      :width="250"
      :loop="false"
      :autoPlay="true"
    />
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Vue3Lottie } from 'vue3-lottie';
import { useAuth } from '@/composables/useAuth';
import { validateUsername, validateEmail, validatePassword, getPasswordStrength } from '@/utils/validators';
import { showSuccess } from '@/utils/toast';

export default {
  name: 'LoginView',
  components: { Vue3Lottie },

  setup() {
    const router = useRouter();
    const { loginUser, registerUser, isLoading } = useAuth();

    const isLogin = ref(true);
    const errorMessage = ref('');
    const showLoginSuccess = ref(false);

    // Login data
    const credentials = ref({
      username: '',
      password: ''
    });

    // Register data
    const registerData = ref({
      username: '',
      email: '',
      full_name: '',
      password: ''
    });

    const confirmPassword = ref('');
    const errors = ref({});
    const passwordStrength = ref('weak');

    function switchToLogin() {
      isLogin.value = true;
      errorMessage.value = '';
      errors.value = {};
    }

    function switchToRegister() {
      isLogin.value = false;
      errorMessage.value = '';
      errors.value = {};
    }

    function checkPasswordStrength() {
      passwordStrength.value = getPasswordStrength(registerData.value.password);
    }

    function validateField(field) {
      errors.value[field] = '';

      if (field === 'username') {
        const username = registerData.value.username?.trim() || '';
        if (!validateUsername(username)) {
          errors.value.username = 'Username must be at least 3 characters and alphanumeric';
        }
      } else if (field === 'email') {
        const email = registerData.value.email?.trim() || '';
        if (!validateEmail(email)) {
          errors.value.email = 'Please enter a valid email address';
        }
      } else if (field === 'password') {
        if (!validatePassword(registerData.value.password)) {
          errors.value.password = 'Password must be at least 8 characters';
        }
      } else if (field === 'confirm_password') {
        if (registerData.value.password !== confirmPassword.value) {
          errors.value.confirm_password = 'Passwords do not match';
        }
      }
    }

    const handleLogin = async () => {
      errorMessage.value = '';

      if (!credentials.value.username || !credentials.value.password) {
        errorMessage.value = 'Please enter username and password';
        return;
      }

      const result = await loginUser(
        credentials.value.username,
        credentials.value.password
      );

      if (result.success) {
        showLoginSuccess.value = true;
        setTimeout(() => {
          showLoginSuccess.value = false;
          router.push('/');
        }, 2500);
      } else {
        errorMessage.value = result.error || 'Login failed. Please try again.';
      }
    };

    async function handleRegister() {
      errorMessage.value = '';
      errors.value = {};

      // Validate all fields
      validateField('username');
      validateField('email');
      validateField('password');
      
      if (registerData.value.password !== confirmPassword.value) {
        errors.value.confirm_password = 'Passwords do not match';
      }

      if (Object.values(errors.value).some(error => error)) {
        return;
      }

      const result = await registerUser(registerData.value);

      if (result.success) {
        // Clear register form
        registerData.value = {
          username: '',
          email: '',
          full_name: '',
          password: ''
        };
        confirmPassword.value = '';
        
        // Show success message
        showLoginSuccess.value = true;
        
        // After animation, switch to login form
        setTimeout(() => {
          showLoginSuccess.value = false;
          isLogin.value = true;
          errorMessage.value = '';
          // Show success toast
          showSuccess('Registration successful! Please login with your credentials.');
        }, 2500);
      } else {
        errorMessage.value = result.error || 'Registration failed. Please try again.';
      }
    }

    return {
      isLogin,
      credentials,
      registerData,
      confirmPassword,
      errors,
      passwordStrength,
      errorMessage,
      isLoading,
      showLoginSuccess,
      switchToLogin,
      switchToRegister,
      handleLogin,
      handleRegister,
      validateField,
      checkPasswordStrength
    };
  }
};

</script>
